// packages/core/src/schedule/storage.ts
//
// Frontière entre le modèle interne V2 (`Range[]` par date) et le format de
// stockage `timeSlotsByDate` lu par `buildPollOptions`
// (packages/core/src/lib/buildPollOptions.ts) et son port Deno
// (supabase/functions/_shared/resolve-date-options.ts).
//
// C'est ici, et UNIQUEMENT ici, que vit la promesse de D23 : V2 doit pouvoir
// lire n'importe quel sondage V1 (mergeMode true ou false, modèle cellule ou
// modèle bloc) sans changer ses options de vote. `fromStorage` reproduit
// EXACTEMENT la logique de regroupement de `buildPollOptions` (mêmes règles
// de contiguïté, même repli de durée sur la granularité) ; `toStorage`
// émet toujours un format « mergeMode: false » explicite (D34a) que
// `buildPollOptions` restitue à l'identique sans avoir besoin de refusionner.
// Le test de propriété (`storage.property.test.ts`) vérifie cette promesse
// par génération aléatoire + `buildPollOptions` réel, comme l'exige D23.

import { sortRanges } from "./intervals";
import type {
  DayRangesMap,
  Range,
  StoredSlot,
  StoredTimeSlotsByDate,
} from "./types";

/**
 * Ce que `dayToStorage`/`toStorage` émettent réellement : toujours
 * `duration` et `label` renseignés (D34a), contrairement à `StoredSlot` en
 * lecture qui accepte le format V1 « modèle cellule » sans ces champs.
 * Assignable à `TimeSlot` (packages/core/src/services/PollCreatorService.ts,
 * `label: string` requis) sans cast à la frontière avec PollCreator.tsx.
 */
export interface EmittedSlot extends StoredSlot {
  duration: number;
  label: string;
}

/**
 * Reconstruit les plages internes d'UN jour à partir de ses créneaux
 * stockés. Reproduit le regroupement de `buildPollOptions` :
 * - seuls les créneaux `enabled` comptent ;
 * - triés chronologiquement ;
 * - en mode fusion (mergeMode true), deux créneaux consécutifs (fin de l'un
 *   = début du suivant) sont regroupés en une seule plage ;
 * - la durée d'un créneau est `slot.duration` s'il est défini et > 0,
 *   sinon la granularité (repli « modèle cellule »).
 */
export function dayFromStorage(
  slots: StoredSlot[] | undefined,
  granularity: number,
  mergeMode: boolean,
): Range[] {
  const fallbackDuration = granularity > 0 ? granularity : 30;
  const enabled = (slots ?? [])
    .filter((s) => s.enabled)
    .slice()
    .sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));

  const ranges: Range[] = [];
  for (const slot of enabled) {
    const duration =
      slot.duration != null && slot.duration > 0
        ? slot.duration
        : fallbackDuration;
    const start = slot.hour * 60 + slot.minute;
    const end = start + duration;

    const last = ranges[ranges.length - 1];
    if (mergeMode && last && last.end === start) {
      last.end = end;
      continue;
    }
    ranges.push({ start, end });
  }
  return ranges;
}

/** `dayFromStorage` sur toutes les dates de `timeSlotsByDate`. */
export function fromStorage(
  timeSlotsByDate: StoredTimeSlotsByDate,
  granularity: number,
  mergeMode: boolean,
): DayRangesMap {
  const out: DayRangesMap = {};
  for (const date of Object.keys(timeSlotsByDate)) {
    out[date] = dayFromStorage(timeSlotsByDate[date], granularity, mergeMode);
  }
  return out;
}

/**
 * Émet les créneaux stockés pour UN jour : un créneau par plage, avec
 * `duration` explicite et `enabled: true` (D34a). Ordre chronologique.
 * Un jour sans plage émet un tableau vide (jamais `undefined`), pour que
 * `buildPollOptions` retombe sur son option « journée entière » (D6).
 */
export function dayToStorage(ranges: Range[]): EmittedSlot[] {
  return sortRanges(ranges).map((r) => {
    const hour = Math.floor(r.start / 60);
    const minute = r.start % 60;
    return {
      hour,
      minute,
      enabled: true,
      duration: r.end - r.start,
      // Requis par le type `TimeSlot` V1 (PollCreatorService.ts) à la
      // frontière ; buildPollOptions ne lit jamais ce champ.
      label: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
    };
  });
}

/**
 * `dayToStorage` pour toutes les dates de `dayRanges`, en garantissant une
 * entrée (même vide) pour chaque date de `selectedDates` — y compris une
 * date qui n'a pas encore d'entrée dans `dayRanges` (D34a : « émet `[]` pour
 * un jour sélectionné sans plage »).
 */
export function toStorage(
  dayRanges: DayRangesMap,
  selectedDates: string[],
): Record<string, EmittedSlot[]> {
  const out: Record<string, EmittedSlot[]> = {};
  for (const date of selectedDates) {
    out[date] = dayToStorage(dayRanges[date] ?? []);
  }
  return out;
}
